import { Suspense } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import classNames from "classnames";
import { Button } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  AssetType,
  draftFocusNodeIdAtom,
  fetchChatbotAtomFamily_SUSPENDS,
  fetchInterfaceProjectAtomFamily_SUSPENDS,
  fetchTableFieldsAtomFamily_SUSPENDS,
  isFocusModeEnabledAtomFamily,
  previewNodeIdAtom,
  togglesAtomResettable,
} from "@zapier/canvas-state";
import { isDraftOrTemplateNode } from "../../../../../utils/isDraftOrTemplateNode";
import styles from "./AssetDetailsButton.module.css";
import { useComposer } from "@zapier/agent-system";
import { FocusIcon } from "../../../../../components/icons/FocusIcon";

type Props = {
  id: string;
  assetType: AssetType;
  assetId: string;
  interfaceProjectId: string;
  hoveringOverAssetDetailsButton: boolean;
};

function SuspendedComponent({
  id,
  assetType,
  assetId,
  interfaceProjectId,
  hoveringOverAssetDetailsButton,
}: Props) {
  const tableId = assetType === "table" ? assetId : undefined;
  const interfaceId =
    assetType === "interface" ? interfaceProjectId : undefined;
  const chatbotId = assetType === "chatbot" ? assetId : undefined;
  const composerService = useComposer();
  const tableFields = useAtomValue(
    fetchTableFieldsAtomFamily_SUSPENDS(tableId),
  );
  const interfaceProject = useAtomValue(
    fetchInterfaceProjectAtomFamily_SUSPENDS(interfaceId),
  );
  const chatbot = useAtomValue(fetchChatbotAtomFamily_SUSPENDS(chatbotId));
  const isDraftNode = isDraftOrTemplateNode(id);
  const toggles = useAtomValue(togglesAtomResettable);
  const setDraftFocusNodeId = useSetAtom(draftFocusNodeIdAtom);
  const setPreviewNodeId = useSetAtom(previewNodeIdAtom);
  const isFocusModeEnabled = useAtomValue(
    isFocusModeEnabledAtomFamily(assetType),
  );

  const openModal = () => {
    if (isDraftNode) {
      setDraftFocusNodeId(id);
      if (toggles.aiComposerDataPills) {
        composerService?.setAdditionalData(
          (existingData: Record<string, any> | undefined) => ({
            ...existingData,
            [`viewing: ${assetType}`]: `User is viewing ${assetType} ${assetId}`,
          }),
        );

        // Set a relevant placeholder based on asset type
        const getPlaceholderForAsset = () => {
          switch (assetType) {
            case "table":
              return `Ask me about this table or request changes...`;
            case "interface":
              return `Ask me about this interface or request changes...`;
            case "chatbot":
              return `Ask me about this chatbot or request changes...`;
            default:
              return `Ask me about this ${assetType} or request changes...`;
          }
        };

        composerService?.setPlaceholderText(getPlaceholderForAsset());
      }
    } else {
      setPreviewNodeId(id);
    }
  };

  if (tableFields === null || interfaceProject === null || chatbot === null)
    return null;

  return (
    <span
      className={classNames(styles.buttonWrapper, {
        [styles.hovered]: hoveringOverAssetDetailsButton,
      })}
    >
      <Button
        size="medium"
        variant="primary"
        onPress={openModal}
        iconPosition="start"
      >
        {isFocusModeEnabled ? (
          <FocusIcon width="24px" height="24px" />
        ) : (
          <Icon name="actionEditAI" />
        )}
        {hoveringOverAssetDetailsButton ? "View asset" : null}
      </Button>
    </span>
  );
}

export function AssetDetailsButton({
  id,
  assetType,
  assetId,
  interfaceProjectId,
  hoveringOverAssetDetailsButton,
}: Props) {
  return (
    <Suspense fallback={null}>
      <SuspendedComponent
        id={id}
        assetType={assetType}
        assetId={assetId}
        interfaceProjectId={interfaceProjectId}
        hoveringOverAssetDetailsButton={hoveringOverAssetDetailsButton}
      />
    </Suspense>
  );
}
